import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OrgRoleOption {
  id: 'owner' | 'billing_admin' | 'admin' | 'member' | 'guest';
  emoji: string;
}

interface OrgRoleSelectProps {
  value: OrgRoleOption['id'];
  onChange: (value: OrgRoleOption['id']) => void;
  className?: string;
}

const ORG_ROLES: OrgRoleOption[] = [
  { id: 'owner', emoji: '🛡️' },
  { id: 'billing_admin', emoji: '💳' },
  { id: 'admin', emoji: '🧩' },
  { id: 'member', emoji: '👤' },
  { id: 'guest', emoji: '👁️' }, // Viewer (read-only)
];

export const OrgRoleSelect: React.FC<OrgRoleSelectProps> = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const selected = ORG_ROLES.find(r => r.id === value) || ORG_ROLES[3];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center justify-between w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 ${className}`}
      >
        <span className="flex items-center gap-2">
          <span>{selected.emoji}</span>
          <span className="text-gray-700">{t(`teamMembers.orgRoles.${selected.id}`)}</span>
        </span>
        <ChevronDown className="w-4 h-4 ml-2 text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 z-20 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg">
            <div className="py-1">
              {ORG_ROLES.map(role => (
                <button
                  key={role.id}
                  onClick={() => { onChange(role.id); setIsOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm ${
                    role.id === value ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{role.emoji}</span>
                    <div>
                      <div className="font-medium">{t(`teamMembers.orgRoles.${role.id}`)}</div>
                      <div className="text-xs text-gray-500">{t(`teamMembers.orgRoles.descriptions.${role.id}`)}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OrgRoleSelect;


